// src/services/stripeService.js

import api from './api'; // Assuming this is your configured axios instance
import { loadStripe } from '@stripe/stripe-js';
import { getAuthToken } from './api'; // For getting the Firebase ID token

let stripePromise = null;
const getStripeInstance = () => {
  if (!stripePromise) {
    const publishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
    if (!publishableKey) {
      const errorMsg = 'Stripe publishable key (VITE_STRIPE_PUBLISHABLE_KEY) is not defined in environment variables.';
      console.error(errorMsg);
      throw new Error(errorMsg);
    }
    stripePromise = loadStripe(publishableKey);
  }
  return stripePromise;
};

const ENDPOINTS = {
  createPaymentIntent: '/api/stripe/create-payment-intent',
  createSubscription: '/api/stripe/create-subscription',
  cancelSubscription: '/api/stripe/cancel-subscription',
  getPaymentMethods: '/api/stripe/payment-methods',
  getSubscriptionStatus: '/api/stripe/subscription-status',
  getPricePlans: '/api/stripe/price-plans',
};

// For authenticated requests
const makeAuthenticatedRequest = async (method, endpoint, data = {}) => {
  const token = getAuthToken(); 
  if (!token) {
    console.warn(`Attempted authenticated request to ${endpoint} without a token.`);
    throw new Error('User must be authenticated to perform this action.');
  }
  try {
    const config = { headers: { Authorization: `Bearer ${token}` } };
    let response;
    if (method === 'get') {
      response = await api.get(endpoint, { ...config, params: data });
    } else if (method === 'post') {
      response = await api.post(endpoint, data, config);
    } else if (method === 'put') {
      response = await api.put(endpoint, data, config);
    } else {
      throw new Error(`Unsupported method: ${method}`);
    }
    return response.data;
  } catch (error) {
    console.error(`Error during ${method.toUpperCase()} request to ${endpoint}:`, error.response?.data || error.message);
    throw new Error(error.response?.data?.error || error.message || `API request to ${endpoint} failed.`);
  }
};

// Public request for getting price plans
export const getPricePlans = async () => {
  try {
    const response = await api.get(ENDPOINTS.getPricePlans);
    // The backend now correctly returns `{ plans: [...] }`.
    // We pass this entire object to the context.
    if (response.data && Array.isArray(response.data.plans)) {
        return response.data;
    } else {
        console.error('Invalid plans data structure received from backend:', response.data);
        throw new Error('Failed to retrieve valid plan data.');
    }
  } catch (error) {
    console.error(`Error during public GET request to ${ENDPOINTS.getPricePlans}:`, error.response?.data || error.message);
    const errorMessage = error.response?.data?.error || error.message || `API request to ${ENDPOINTS.getPricePlans} failed.`;
    throw new Error(errorMessage);
  }
};


// Authenticated functions
export const createPaymentIntent = async (amount, currency = 'eur', productId = null, description = null) => {
  return makeAuthenticatedRequest('post', ENDPOINTS.createPaymentIntent, { amount, currency, productId, description });
};

export const createSubscription = async (planId, paymentMethodId) => { // planId is Stripe Price ID
  return makeAuthenticatedRequest('post', ENDPOINTS.createSubscription, { planId, paymentMethodId });
};

export const cancelSubscription = async (subscriptionId) => {
  return makeAuthenticatedRequest('post', ENDPOINTS.cancelSubscription, { subscriptionId });
};

export const getPaymentMethods = async () => {
  return makeAuthenticatedRequest('get', ENDPOINTS.getPaymentMethods);
};

export const getSubscriptionStatus = async () => {
  return makeAuthenticatedRequest('get', ENDPOINTS.getSubscriptionStatus);
};


export { getStripeInstance as getStripe };

export default {
  getStripe: getStripeInstance,
  createPaymentIntent,
  createSubscription,
  cancelSubscription,
  getPaymentMethods,
  getSubscriptionStatus,
  getPricePlans,
};